import { DeliveryPlanRow } from '../presentation/features/delivery/MyDeliveryPlanTable/types';
import { formatDate, formatNumberWithDecimals } from './localeFormatter';

interface TableTranslations {
  title: string;
  deliveryDate: string;
  fieldName: string;
  area: string;
  load: string;
  transporter: string;
  pileCoverer: string;
  calendarWeek: string;
  ownChoice: string;
}

interface TableColumn {
  field: keyof DeliveryPlanRow;
  headerName: string;
  formatter?: (
    value: string | number | null | undefined,
    row?: DeliveryPlanRow
  ) => string;
}

const COUNTRIES_WITH_PILE_COVERER = ['de-DE', 'da-DK', 'pl-PL'];
const PILE_COVERER_COLUMN_NAME = 'pileCoverer' as keyof DeliveryPlanRow;

export const generateTableHTML = (
  data: DeliveryPlanRow[],
  countryIsoCode: string,
  translations: TableTranslations,
  activeBpNumber: string
): string => {
  const baseColumns: TableColumn[] = [
    {
      field: 'loadDate',
      headerName: translations.deliveryDate,
      formatter: (value, row) => {
        if (!value) return '-';
        if (row?.exactDateVisible) {
          return formatDate(value as string, countryIsoCode);
        } else {
          return `${translations.calendarWeek} ${row?.loadWeek}`;
        }
      },
    },
    {
      field: 'fieldName',
      headerName: translations.fieldName,
      formatter: (value, row) => {
        if (row?.isSelfLoader) {
          return translations.ownChoice;
        }
        return value?.toString() || '-';
      },
    },
    {
      field: 'acreage',
      headerName: translations.area,
      formatter: (value) => {
        if (!value && value !== 0) return '-';
        return formatNumberWithDecimals(value as number, countryIsoCode, 1);
      },
    },
    {
      field: 'loadQuantity',
      headerName: translations.load,
      formatter: (value) => {
        if (!value && value !== 0) return '-';
        return formatNumberWithDecimals(value as number, countryIsoCode, 0);
      },
    },
    {
      field: 'transportingCompany',
      headerName: translations.transporter,
      formatter: (value, row) => {
        if (row?.isSelfLoader) return '-';
        return row?.transportingCompanyName || '-';
      },
    },
  ];

  if (COUNTRIES_WITH_PILE_COVERER.includes(countryIsoCode || '')) {
    baseColumns.push({
      field: PILE_COVERER_COLUMN_NAME,
      headerName: translations.pileCoverer,
      formatter: (value, row) => {
        return row?.pileCovererName || '-';
      },
    });
  }

  let html = `<h2>${activeBpNumber} ${translations.title}</h2>`;
  html += '<table>';
  html += '<thead><tr>';
  baseColumns.forEach((col) => {
    html += `<th>${col.headerName}</th>`;
  });
  html += '</tr></thead>';

  html += '<tbody>';
  data.forEach((row) => {
    html += '<tr>';
    baseColumns.forEach((col) => {
      const value = row[col.field];
      const displayValue = col.formatter
        ? col.formatter(value, row)
        : value !== null && value !== undefined
          ? String(value)
          : '-';
      html += `<td>${displayValue}</td>`;
    });
    html += '</tr>';
  });
  html += '</tbody></table>';

  return html;
};

export const handleDownloadClick = (
  data: DeliveryPlanRow[],
  countryIsoCode: string,
  translations: TableTranslations,
  activeBpNumber: string
) => {
  const tableHTML = generateTableHTML(
    data,
    countryIsoCode,
    translations,
    activeBpNumber
  );

  const newWindow = window.open('', '', 'height=600,width=800');

  if (newWindow) {
    newWindow.document.write('<html>');
    newWindow.document.write('<head>');
    newWindow.document.write('<style>');
    newWindow.document.write(`
      table { 
        width: 100%; 
        border-collapse: collapse; 
        margin-bottom: 20px; 
      }
      th, td { 
        border: 1px solid black; 
        padding: 8px; 
        text-align: left; 
      }
      th { 
        background-color: #f2f2f2; 
        font-weight: bold; 
      }
      h2 { 
        margin-bottom: 20px; 
      }
      @media print { 
        @page { 
          margin: 0; 
        } 
        body { 
          margin: 1cm; 
        }
        th { 
          background-color: #f2f2f2 !important; 
          -webkit-print-color-adjust: exact; 
        }
      }
    `);
    newWindow.document.write('</style>');
    newWindow.document.write('</head>');
    newWindow.document.write('<body>');
    newWindow.document.write(tableHTML);
    newWindow.document.write('</body></html>');

    newWindow.document.close();

    newWindow.onload = () => {
      newWindow.print();
      newWindow.close();
    };
  } else {
    alert(
      'Cannot open a new window for printing. Check your browser settings.'
    );
  }
};
